import { parseISO } from 'date-fns';
import { LuCalendar, LuCircleCheck, LuListTodo, LuMail, LuStickyNote } from 'react-icons/lu';

import { R1WeekInReviewPayload } from '@ndvr/customer-api';

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { TextLink } from '@/widgets';
import { useHouseholdRoutePaths } from 'utils';

interface WeekInReviewSummaryProps {
    payload: R1WeekInReviewPayload;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const formatRange = (weekStart: string, weekEnd: string): string => {
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const start = parseISO(weekStart).toLocaleDateString('en-US', opts);
    const end = parseISO(weekEnd).toLocaleDateString('en-US', { ...opts, year: 'numeric' });
    return `${start} – ${end}`;
};

export const WeekInReviewSummary = ({ payload, open, onOpenChange }: WeekInReviewSummaryProps) => {
    const { HouseholdIndividualPageRoutePath } = useHouseholdRoutePaths();
    const householdLink = (householdGUID: string) => HouseholdIndividualPageRoutePath(householdGUID);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-3/4 overflow-y-auto sm:w-[28rem]" aria-describedby={undefined}>
                <SheetHeader className="mb-4">
                    <SheetTitle>Week in Review</SheetTitle>
                    <SheetDescription>{formatRange(payload.weekStart, payload.weekEnd)}</SheetDescription>
                </SheetHeader>

                {payload.summary && <p className="text-cocoa-700 mb-6 text-sm leading-relaxed">{payload.summary}</p>}

                <Section icon={<LuCalendar className="h-4 w-4" />} title="Meetings" count={payload.meetingCount}>
                    {payload.meetings.map((meeting) => (
                        <Row key={meeting.eventGUID} to={householdLink(meeting.householdGUID)} primary={meeting.title}>
                            {meeting.keyTopics && <span className="text-cocoa-500 text-xs">{meeting.keyTopics}</span>}
                        </Row>
                    ))}
                </Section>

                <Section
                    icon={<LuMail className="h-4 w-4" />}
                    title="Emails by relationship"
                    count={payload.emailsByRelationship.length}
                >
                    {payload.emailsByRelationship.map((relationship) => (
                        <Row
                            key={relationship.householdGUID}
                            to={householdLink(relationship.householdGUID)}
                            primary={relationship.relationshipName}
                        >
                            <span className="text-cocoa-500 text-xs">
                                {relationship.sentCount} sent · {relationship.receivedCount} received
                            </span>
                        </Row>
                    ))}
                </Section>

                <Section
                    icon={<LuCircleCheck className="h-4 w-4" />}
                    title="Tasks completed"
                    count={payload.completedTasks.length}
                >
                    {payload.completedTasks.map((task) => (
                        <Row key={task.taskGUID} to={householdLink(task.householdGUID)} primary={task.title} />
                    ))}
                </Section>

                <Section
                    icon={<LuListTodo className="h-4 w-4" />}
                    title="Open follow-ups"
                    count={payload.openTasks.length}
                >
                    {payload.openTasks.map((task) => (
                        <Row key={task.taskGUID} to={householdLink(task.householdGUID)} primary={task.title} />
                    ))}
                </Section>

                <Section icon={<LuStickyNote className="h-4 w-4" />} title="New notes" count={payload.newNotes.length}>
                    {payload.newNotes.map((note) => (
                        <Row key={note.noteGUID} to={householdLink(note.householdGUID)} primary={note.subject} />
                    ))}
                </Section>
            </SheetContent>
        </Sheet>
    );
};

const Section = ({
    icon,
    title,
    count,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    count: number;
    children: React.ReactNode;
}) => (
    <div className="mb-6">
        <div className="text-cocoa-1000 mb-2 flex items-center gap-2 text-sm font-medium">
            <span className="text-cocoa-700">{icon}</span>
            {title}
            <span className="text-cocoa-500">({count})</span>
        </div>
        {count > 0 ? <div className="flex flex-col gap-2">{children}</div> : <EmptyRow />}
    </div>
);

const Row = ({ to, primary, children }: { to: string; primary: string; children?: React.ReactNode }) => (
    <div className="border-border flex flex-col rounded-md border px-3 py-2">
        <TextLink to={to}>{primary}</TextLink>
        {children}
    </div>
);

const EmptyRow = () => <p className="text-cocoa-500 pl-6 text-xs">None this week</p>;
